import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Clock, Play, Pause, Square, Plus, Trash2, Pencil, Check, X, Calendar } from 'lucide-react';
import { timeTrackingService } from '../../services/api';

const TENTHS_PRESETS = [0.1, 0.2, 0.3, 0.5, 1.0, 2.0];

const formatTimerDisplay = (totalSeconds) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const formatHoursDecimal = (hours) => {
  const num = parseFloat(hours);
  return isNaN(num) ? '0.0' : num.toFixed(1);
};

// Round up to nearest tenth of an hour (.1 rule: 0-6 min = .1, 7-12 min = .2, etc.)
const roundUpToTenthHour = (totalSeconds) => {
  if (totalSeconds <= 0) return '0.0';
  const tenths = Math.ceil(totalSeconds / 360);
  return (tenths / 10).toFixed(1);
};

const CaseTimeTracking = ({ caseId, onSuccess, onError }) => {
  // Timer state
  const [timer, setTimer] = useState(null); // null = no active timer for this case
  const [displaySeconds, setDisplaySeconds] = useState(0);
  const intervalRef = useRef(null);

  // Entry form state
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [entryHours, setEntryHours] = useState('');
  const [entryDescription, setEntryDescription] = useState('');
  const [entryDate, setEntryDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);

  // Entries list
  const [entries, setEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(true);

  // Discard confirmation
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Edit state
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [editHours, setEditHours] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editDate, setEditDate] = useState('');

  const startLocalTick = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setDisplaySeconds(prev => prev + 1);
    }, 1000);
  }, []);

  const stopLocalTick = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const fetchEntries = useCallback(async () => {
    try {
      setLoadingEntries(true);
      const res = await timeTrackingService.listEntries({ case_id: caseId, limit: 20 });
      setEntries(res.data);
    } catch {
      // ignore
    } finally {
      setLoadingEntries(false);
    }
  }, [caseId]);

  // Fetch active timers and entries on mount
  useEffect(() => {
    const fetchTimer = async () => {
      try {
        const res = await timeTrackingService.getTimers();
        const allTimers = res.data || [];
        const caseTimer = allTimers.find(t => String(t.case_id) === String(caseId));
        if (caseTimer) {
          setTimer(caseTimer);
          let secs = caseTimer.accumulated_seconds || 0;
          if (caseTimer.is_running && caseTimer.started_at) {
            secs += Math.floor((Date.now() - new Date(caseTimer.started_at).getTime()) / 1000);
            startLocalTick();
          }
          setDisplaySeconds(secs);
        }
      } catch {
        // No timer active
      }
    };
    fetchTimer();
    fetchEntries();
    return () => stopLocalTick();
  }, [caseId, startLocalTick, stopLocalTick, fetchEntries]);

  const handleStartTimer = async () => {
    try {
      const res = await timeTrackingService.startTimer({ case_id: caseId });
      setTimer(res.data);
      setDisplaySeconds(0);
      startLocalTick();
    } catch (err) {
      onError?.('Failed to start timer');
    }
  };

  const handlePauseTimer = async () => {
    try {
      const res = await timeTrackingService.pauseTimer(timer.id);
      setTimer(res.data);
      setDisplaySeconds(res.data.accumulated_seconds);
      stopLocalTick();
    } catch (err) {
      onError?.('Failed to pause timer');
    }
  };

  const handleResumeTimer = async () => {
    try {
      const res = await timeTrackingService.resumeTimer(timer.id);
      setTimer(res.data);
      setDisplaySeconds(res.data.accumulated_seconds);
      startLocalTick();
    } catch (err) {
      onError?.('Failed to resume timer');
    }
  };

  const handleStopTimer = async () => {
    try {
      stopLocalTick();
      await timeTrackingService.stopTimer(timer.id);
      setTimer(null);
      setDisplaySeconds(0);
      onSuccess?.('Time entry saved');
      fetchEntries();
    } catch (err) {
      onError?.('Failed to stop timer');
    }
  };

  const handleDiscardClick = () => {
    setShowDiscardConfirm(true);
  };

  const handleDiscardConfirm = async () => {
    setShowDiscardConfirm(false);
    try {
      stopLocalTick();
      await timeTrackingService.discardTimer(timer.id);
      setTimer(null);
      setDisplaySeconds(0);
    } catch (err) {
      onError?.('Failed to discard timer');
    }
  };

  const handleDiscardCancel = () => {
    setShowDiscardConfirm(false);
  };

  const handleLogEntry = async () => {
    const hours = parseFloat(entryHours);
    if (!hours || hours <= 0) {
      onError?.('Please enter a valid time amount');
      return;
    }
    try {
      setSubmitting(true);
      await timeTrackingService.createEntry({
        hours,
        description: entryDescription || null,
        case_id: caseId,
        is_billable: true,
        logged_at: new Date(entryDate + 'T12:00:00').toISOString(),
      });
      setEntryHours('');
      setEntryDescription('');
      setEntryDate(new Date().toISOString().slice(0, 10));
      setShowEntryForm(false);
      onSuccess?.('Time entry logged');
      fetchEntries();
    } catch {
      onError?.('Failed to log time entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      await timeTrackingService.deleteEntry(entryId);
      fetchEntries();
      onSuccess?.('Time entry deleted');
    } catch {
      onError?.('Failed to delete time entry');
    }
  };

  const startEditing = (entry) => {
    setEditingEntryId(entry.id);
    setEditHours(parseFloat(entry.hours).toFixed(1));
    setEditDescription(entry.description || '');
    setEditDate(new Date(entry.logged_at).toISOString().slice(0, 10));
  };

  const cancelEditing = () => {
    setEditingEntryId(null);
    setEditHours('');
    setEditDescription('');
    setEditDate('');
  };

  const handleSaveEdit = async () => {
    const hours = parseFloat(editHours);
    if (!hours || hours <= 0) {
      onError?.('Please enter a valid time amount');
      return;
    }
    try {
      setSubmitting(true);
      await timeTrackingService.updateEntry(editingEntryId, {
        hours,
        description: editDescription || null,
        logged_at: new Date(editDate + 'T12:00:00').toISOString(),
      });
      setEditingEntryId(null);
      onSuccess?.('Time entry updated');
      fetchEntries();
    } catch {
      onError?.('Failed to update time entry');
    } finally {
      setSubmitting(false);
    }
  };

  const totalHours = entries.reduce((sum, e) => sum + parseFloat(e.hours), 0);
  const isTimerForThisCase = timer && String(timer.case_id) === String(caseId);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-4 flex items-center">
        <Clock className="mr-2 h-5 w-5" />
        Time Tracking
      </h3>

      {/* Timer Section */}
      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-2xl font-bold text-gray-900">
              {formatTimerDisplay(displaySeconds)}
            </div>
            <div className="text-sm text-gray-500">
              {roundUpToTenthHour(displaySeconds)} hrs
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {!isTimerForThisCase ? (
              <button
                onClick={handleStartTimer}
                className="bg-green-600 text-white p-2 rounded-lg hover:bg-green-700"
                title="Start timer"
              >
                <Play className="h-5 w-5" />
              </button>
            ) : timer.is_running ? (
              <>
                <button
                  onClick={handlePauseTimer}
                  className="bg-yellow-500 text-white p-2 rounded-lg hover:bg-yellow-600"
                  title="Pause timer"
                >
                  <Pause className="h-5 w-5" />
                </button>
                <button
                  onClick={handleStopTimer}
                  className="bg-red-600 text-white p-2 rounded-lg hover:bg-red-700"
                  title="Stop and save"
                >
                  <Square className="h-5 w-5" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleResumeTimer}
                  className="bg-green-600 text-white p-2 rounded-lg hover:bg-green-700"
                  title="Resume timer"
                >
                  <Play className="h-5 w-5" />
                </button>
                <button
                  onClick={handleStopTimer}
                  className="bg-red-600 text-white p-2 rounded-lg hover:bg-red-700"
                  title="Stop and save"
                >
                  <Square className="h-5 w-5" />
                </button>
                <button
                  onClick={handleDiscardClick}
                  className="text-gray-400 hover:text-gray-600 p-2"
                  title="Discard timer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>
        {showDiscardConfirm && (
          <div className="flex items-center justify-between mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <span className="text-sm text-red-700">Discard this timer? Time will not be saved.</span>
            <div className="flex items-center space-x-2 ml-2">
              <button onClick={handleDiscardConfirm} className="text-red-600 hover:text-red-800 text-sm font-medium px-2 py-0.5 rounded hover:bg-red-100">
                Discard
              </button>
              <button onClick={handleDiscardCancel} className="text-gray-500 hover:text-gray-700 text-sm px-2 py-0.5 rounded hover:bg-gray-100">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Manual Entry */}
      {!showEntryForm ? (
        <button
          onClick={() => setShowEntryForm(true)}
          className="w-full text-left px-3 py-2 rounded border border-dashed border-gray-300 hover:border-gray-400 hover:bg-gray-50 flex items-center text-gray-600 text-sm mb-4"
        >
          <Plus className="mr-2 h-4 w-4" />
          Log Time
        </button>
      ) : (
        <div className="border border-gray-200 rounded-lg p-4 mb-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hours (tenths)</label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={entryHours}
                onChange={(e) => setEntryHours(e.target.value)}
                className="w-24 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                placeholder="0.0"
                autoFocus
              />
              <div className="flex flex-wrap gap-1">
                {TENTHS_PRESETS.map(val => (
                  <button
                    key={val}
                    onClick={() => setEntryHours(String(val))}
                    className={`px-2 py-1 rounded text-xs border ${
                      entryHours === String(val)
                        ? 'bg-yellow-100 border-yellow-400 text-yellow-800'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {val}h
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={entryDescription}
              onChange={(e) => setEntryDescription(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
              placeholder="What did you work on?"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-gray-400" />
              <input
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleLogEntry}
              disabled={submitting}
              className="bg-yellow-500 text-white px-4 py-2 rounded text-sm hover:bg-yellow-600 disabled:opacity-50"
            >
              {submitting ? 'Saving...' : 'Log Time'}
            </button>
            <button
              onClick={() => { setShowEntryForm(false); setEntryHours(''); setEntryDescription(''); setEntryDate(new Date().toISOString().slice(0, 10)); }}
              className="text-gray-600 px-4 py-2 rounded text-sm hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="flex justify-between text-sm text-gray-600 mb-3 px-1">
        <span>Total Logged</span>
        <span className="font-semibold text-gray-900">{formatHoursDecimal(totalHours)} hrs</span>
      </div>

      {/* Recent Entries */}
      {loadingEntries ? (
        <div className="text-sm text-gray-400 text-center py-2">Loading...</div>
      ) : entries.length === 0 ? (
        <div className="text-sm text-gray-400 text-center py-2">No time entries yet</div>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {entries.map(entry => (
            editingEntryId === entry.id ? (
              <div key={entry.id} className="border border-yellow-300 bg-yellow-50 rounded-lg p-3 space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={editHours}
                    onChange={(e) => setEditHours(e.target.value)}
                    className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    autoFocus
                  />
                  <div className="flex flex-wrap gap-1">
                    {TENTHS_PRESETS.map(val => (
                      <button
                        key={val}
                        onClick={() => setEditHours(String(val))}
                        className={`px-2 py-0.5 rounded text-xs border ${
                          editHours === String(val)
                            ? 'bg-yellow-100 border-yellow-400 text-yellow-800'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {val}h
                      </button>
                    ))}
                  </div>
                </div>
                <input
                  type="text"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Description"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') cancelEditing(); }}
                />
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <input
                    type="date"
                    value={editDate}
                    onChange={(e) => setEditDate(e.target.value)}
                    className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleSaveEdit}
                    disabled={submitting}
                    className="bg-yellow-500 text-white px-3 py-1 rounded text-sm hover:bg-yellow-600 disabled:opacity-50 flex items-center"
                  >
                    <Check className="h-3.5 w-3.5 mr-1" />
                    {submitting ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={cancelEditing}
                    className="text-gray-600 px-3 py-1 rounded text-sm hover:bg-gray-100 flex items-center"
                  >
                    <X className="h-3.5 w-3.5 mr-1" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div key={entry.id} className="flex items-center justify-between py-2 px-2 rounded hover:bg-gray-50 group">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900">{formatHoursDecimal(entry.hours)}h</span>
                    {entry.description && (
                      <span className="text-sm text-gray-600 truncate">{entry.description}</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400">
                    {entry.user_name} &middot; {new Date(entry.logged_at).toLocaleDateString()}
                  </div>
                </div>
                {!entry.is_billed && (
                  <div className="flex items-center opacity-0 group-hover:opacity-100">
                    <button
                      onClick={() => startEditing(entry)}
                      className="text-gray-300 hover:text-yellow-600 p-1"
                      title="Edit entry"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeleteEntry(entry.id)}
                      className="text-gray-300 hover:text-red-500 p-1"
                      title="Delete entry"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
};

export default CaseTimeTracking;
